import { Link } from 'react-router-dom'
import SEO from '../components/SEO.jsx'
import { useProducts } from '../context/ProductsContext.jsx'
import './TermsPage.css'

export const DEFAULT_TERMS_SECTIONS = [
  {
    title: '1. Research Use Only',
    body: `All products sold by LooksLab are supplied strictly for in-vitro research and scientific investigation purposes only. They are not intended for use in humans or animals, for diagnostic purposes, or for any therapeutic application.

By placing an order you confirm that you are a qualified researcher, scientist, or professional purchasing these compounds solely for lawful laboratory research. You assume full responsibility for ensuring your use complies with all applicable local, state, federal, and international laws and regulations.

LooksLab reserves the right to refuse or cancel any order where there is reason to believe the products will be used outside of their intended research context.`,
  },
  {
    title: '2. No Medical Advice — No FDA Approval',
    body: `Nothing on this website or in any communication from LooksLab constitutes medical advice, a medical claim, or a recommendation for human use. LooksLab is not a licensed medical provider and does not offer medical guidance of any kind.

None of the products sold by LooksLab have been evaluated or approved by the U.S. Food and Drug Administration (FDA) or any other regulatory body. These products are not intended to diagnose, treat, cure, or prevent any disease or medical condition.

LooksLab makes no representations or warranties regarding the efficacy, safety, or suitability of any product for any purpose other than in-vitro research. You should not rely on any product description, result, or testimonial as a basis for any health-related decision.`,
  },
  {
    title: '3. Payment & Refund Policy',
    body: `All orders require payment in full before fulfillment. Accepted payment methods are listed at checkout and on your invoice. Payment must reference your invoice number to be matched and confirmed.

All sales are final. Due to the nature of research compounds — which require proper handling and storage conditions and cannot be restocked once dispatched — we do not accept returns or issue refunds for change of mind, incorrect orders placed by the customer, or orders that have already shipped.

Exceptions may be made at LooksLab's sole discretion in cases where the product delivered is materially different from what was ordered, the product arrives visibly damaged due to our packaging failure, or an order insurance claim is approved.

To raise a dispute, contact us through Discord within 7 days of your delivery date with your invoice number and supporting documentation. LooksLab reserves the right to cancel any order prior to shipment and issue a full refund at our discretion.`,
  },
  {
    title: '4. Shipping & Liability',
    body: `LooksLab ships all orders with appropriate packaging to ensure product integrity throughout transit. Risk of loss and title for products passes to you upon handoff to the shipping carrier.

Lost or seized packages: LooksLab is not liable for packages lost in transit, delayed by the carrier, or seized by customs or law enforcement. If you purchased order insurance at checkout and your package is lost or seized, submit a claim through Discord and we will reship your order at no additional cost, subject to the insurance terms.

International orders: You are solely responsible for understanding and complying with the import laws of your country. LooksLab does not guarantee delivery to any destination and is not responsible for any customs duties, taxes, or legal consequences arising from import.

Pre-order shipments: For orders containing pre-order items, estimated fulfillment timelines are provided in good faith and are not guaranteed. LooksLab will not be held liable for delays caused by supply chain disruptions, manufacturing schedules, or carrier issues.

In no event shall LooksLab's total liability to you for any claim arising from a transaction exceed the total amount paid by you for the specific order in question.`,
  },
  {
    title: '5. Eligibility',
    body: `By placing an order you represent that you are at least 18 years of age, have the legal authority to enter into this agreement, and are not located in a jurisdiction where the purchase or possession of research compounds is prohibited.`,
  },
  {
    title: '6. Limitation of Liability & Indemnification',
    body: `To the fullest extent permitted by law, LooksLab and its owners, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our products or website.

You agree to indemnify and hold LooksLab harmless from any claims, losses, damages, or expenses (including legal fees) arising from your misuse of any product, your violation of these terms, or your violation of any applicable law.`,
  },
  {
    title: '7. Changes to These Terms',
    body: `LooksLab reserves the right to update these Terms & Conditions at any time. Updated terms will be posted on this page with a revised effective date. Continued use of our website or placement of orders after any update constitutes your acceptance of the revised terms.`,
  },
  {
    title: '8. Contact',
    body: `If you have questions about these terms, reach out through our Discord server or the contact section of our website.`,
  },
]

export const DEFAULT_TERMS_EFFECTIVE_DATE = 'June 2025'

export default function TermsPage() {
  const { catalogSettings } = useProducts()

  const saved         = catalogSettings?.terms_content ?? {}
  const effectiveDate = saved.effectiveDate ?? DEFAULT_TERMS_EFFECTIVE_DATE
  const sections      = saved.sections?.length ? saved.sections : DEFAULT_TERMS_SECTIONS

  return (
    <div className="container terms">
      <SEO config={{ title: 'Terms & Conditions', robots: 'noindex, nofollow', titleRaw: true }} />
      <h1>Terms &amp; Conditions</h1>
      <p className="terms__updated">Effective date: {effectiveDate} · Last updated: {effectiveDate}</p>

      {sections.map((sec, i) => (
        <section key={i}>
          <h2>{sec.title}</h2>
          {sec.body.split('\n\n').map((para, pi) => (
            <p key={pi}>{para}</p>
          ))}
        </section>
      ))}

      <p style={{ marginTop: '1.5rem' }}>
        <Link to="/" className="terms__back">← Back to home</Link>
      </p>
    </div>
  )
}
